import type { AsaRequestConfig } from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every(item => typeof item === 'string') ? [...value] : undefined;
}

function positiveInteger(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
}

/** Forward only the documented host-supplied ASA request keys. */
export function normalizeAsaRequestConfig(value: unknown): AsaRequestConfig {
  if (!isRecord(value)) return {};
  const config: AsaRequestConfig = {};
  if (typeof value.domain === 'string' && value.domain.trim()) config.domain = value.domain.trim();
  if (typeof value.guard === 'boolean') config.guard = value.guard;
  const numResultsPerEvent = positiveInteger(value.numResultsPerEvent);
  if (numResultsPerEvent !== undefined) config.numResultsPerEvent = numResultsPerEvent;
  const numResultEvents = positiveInteger(value.numResultEvents);
  if (numResultEvents !== undefined) config.numResultEvents = numResultEvents;
  if (isRecord(value.preFilterExpression)) config.preFilterExpression = {...value.preFilterExpression};
  if (isRecord(value.fmtOptions)) {
    const fields = stringArray(value.fmtOptions.fields);
    const hiddenFields = stringArray(value.fmtOptions.hidden_fields);
    if (fields !== undefined || hiddenFields !== undefined) {
      config.fmtOptions = {
        ...(fields !== undefined && {fields}),
        ...(hiddenFields !== undefined && {hidden_fields: hiddenFields}),
      };
    }
  }
  return config;
}

/** All existing card metadata and fallbacks, plus product-level discount pricing.
 * Live ASA verification confirms `fields` returns the discount against data.id;
 * it is not a hidden field and no grouped facet values are consulted.
 */
export const PRODUCT_CARD_FIELDS = [
  'id', 'name', 'productDisplayName', 'url', 'image_url',
  'sizeInCL', 'size', 'strengthInPC', 'strength',
  'salesPriceIncVat', 'salesPrice', 'price', 'discountedSalesPrice',
  'currency', 'isAvailableToBuy', 'isOutOfStock', 'variation_id',
];
export function withProductPricingFields(config: AsaRequestConfig): AsaRequestConfig {
  const fmt = config.fmtOptions;
  return {
    ...config,
    fmtOptions: {
      ...fmt,
      fields: [...new Set([...(fmt?.fields || []), ...PRODUCT_CARD_FIELDS])],
    },
  };
}
